import { useCallback, useEffect, useRef, useState } from 'react';

const CURRENT_BUILD_ID = process.env.REACT_APP_BUILD_ID || '';
const POLL_MS = 5 * 60 * 1000;

async function fetchRemoteBuildId() {
  const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) return null;
  const data = await res.json();
  return data?.buildId || null;
}

/**
 * Detects when a newer deploy is available (or a chunk failed to load)
 * so the UI can offer a hard refresh.
 */
export default function useAppVersion() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const checkingRef = useRef(false);

  const checkForUpdate = useCallback(async () => {
    if (!CURRENT_BUILD_ID || checkingRef.current) return;
    checkingRef.current = true;
    try {
      const remoteId = await fetchRemoteBuildId();
      if (remoteId && remoteId !== CURRENT_BUILD_ID) {
        setUpdateAvailable(true);
      }
    } catch {
      // Offline / transient network errors — ignore
    } finally {
      checkingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!CURRENT_BUILD_ID) return undefined;

    checkForUpdate();

    const intervalId = setInterval(checkForUpdate, POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') checkForUpdate();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', checkForUpdate);

    const onChunkError = (event) => {
      const msg = event?.reason?.message || event?.message || '';
      if (/Loading chunk [\d]+ failed|ChunkLoadError|Failed to fetch dynamically imported module/i.test(msg)) {
        setUpdateAvailable(true);
      }
    };
    window.addEventListener('unhandledrejection', onChunkError);
    window.addEventListener('error', onChunkError);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', checkForUpdate);
      window.removeEventListener('unhandledrejection', onChunkError);
      window.removeEventListener('error', onChunkError);
    };
  }, [checkForUpdate]);

  const refresh = useCallback(() => {
    window.location.reload();
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  return {
    updateAvailable: updateAvailable && !dismissed,
    refresh,
    dismiss,
    currentBuildId: CURRENT_BUILD_ID,
  };
}
